<template>
    <!-- Global search (GET search/all): jobs, appointments, consumers and protection plans in one paginated list -->
    <div class="w-full font-roboto fitScreenPage">
        <!-- fitScreenFill: at least the space left on the screen (MainLayout fillScreen); more results grow it and the page scrolls -->
        <div
            class="fitScreenFill relative border-1 rounded-2xl min-w-full min-h-[60vh] flex flex-col gap-[10px] px-2 laptop:px-3 pt-4 pb-5 border-gray-300 bg-white bg-opacity-50">
            <!-- one row like the other lists: type filter (multi-select dropdown → types[]; nothing ticked = all types) + Show entries -->
            <!-- on the card's top border, like the work-order lists -->
            <div
                class="flex flex-wrap items-center justify-between gap-[10px] mobile:px-1.5 laptop:px-2.5 mobile:-mt-[40px] laptop:-mt-[45px]">
                <div ref="typeMenu" class="relative">
                    <button type="button" aria-haspopup="listbox" :aria-expanded="typeMenuOpen ? 'true' : 'false'"
                        class="flex items-center justify-between gap-[8px] h-[40px] min-w-[180px] px-[14px] rounded-full border-0 bg-[#fff] !shadow-[0px_0px_10px_0px_#00000026] focus:!shadow-[0px_0px_10px_0px_#00000026] focus:!outline-none text-[13px] text-[#333333] font-roboto"
                        @click="typeMenuOpen = !typeMenuOpen">
                        <span class="flex items-center gap-[8px] min-w-0">
                            <!-- funnel, same as the list filter button -->
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"
                                class="flex-shrink-0">
                                <path d="M4 5h16l-6 7.5V19l-4-2v-4.5L4 5z" stroke="#7286D3" stroke-width="1.8"
                                    stroke-linejoin="round" />
                            </svg>
                            <span class="truncate">{{ typeLabel }}</span>
                        </span>
                        <i class="fa fa-angle-down text-[18px] text-[#5B616B] transition-transform"
                            :class="typeMenuOpen ? 'rotate-180' : ''" />
                    </button>
                    <ul v-if="typeMenuOpen" role="listbox" aria-multiselectable="true"
                        class="absolute left-0 top-[calc(100%+6px)] z-30 min-w-full w-max list-none m-0 p-[6px] rounded-[10px] border border-[#E1E5EF] bg-[#fff] shadow-[0px_4px_12px_0px_#0000001A]">
                        <!-- Select all: ticks every type (all ticked = same as none: every type is searched) -->
                        <li role="option" :aria-selected="allTypesSelected" class="border-b border-[#EFEFEF] mb-[4px] pb-[4px]">
                            <label
                                class="flex items-center gap-[10px] m-0 px-[10px] py-[8px] rounded-[6px] cursor-pointer text-[13px] font-medium text-[#333333] hover:bg-[#F3F4F7]">
                                <input type="checkbox" class="w-[16px] h-[16px] accent-[#7286D3]"
                                    :checked="allTypesSelected" @change="onSelectAllTypes($event)">
                                Select all
                            </label>
                        </li>
                        <li v-for="filter in typeOptions" :key="filter.key" role="option"
                            :aria-selected="isTypeSelected(filter.key)">
                            <label
                                class="flex items-center gap-[10px] m-0 px-[10px] py-[8px] rounded-[6px] cursor-pointer text-[13px] text-[#333333] hover:bg-[#F3F4F7]">
                                <input type="checkbox" class="w-[16px] h-[16px] accent-[#7286D3]"
                                    :checked="isTypeSelected(filter.key)" @change="onTypeChange(filter.key, $event)">
                                {{ filter.text }}
                            </label>
                        </li>
                    </ul>
                </div>
                <entities :model-value="limit" global-entities-class="" @update:model-value="onLimitChange" />
            </div>

            <EmptyState v-if="tooShort" title="Keep typing" min-height="min-h-[50vh]"
                :text="`Type at least ${minLength} characters to search.`" />
            <EmptyState v-else-if="loaded && !items.length" min-height="min-h-[50vh]" />

            <template v-else-if="items.length">
                <!-- Laptop and up: table (same look as customTable.vue) -->
                <div class="hidden laptop:block overflow-x-auto customscrollbar">
                    <table class="w-full border-separate border-spacing-y-1">
                        <thead>
                            <tr>
                                <th v-for="(header, index) in headers" :key="header.key"
                                    class="px-2.5 py-2 !bg-[#BA90C6] font-roboto font-medium text-[12px] largeDesktop:text-[14px] !text-white whitespace-nowrap select-none"
                                    :class="{ 'rounded-l-[10px]': index === 0, 'rounded-r-[10px]': index === headers.length - 1 }">
                                    {{ header.text }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in items" :key="item.type + '-' + item.id"
                                class="shadow-[0_0_5px_0_#00000026] transition-all rounded-[10px]"
                                :class="isClickable(item) ? 'cursor-pointer hover:shadow-md' : 'cursor-default'"
                                @click="onRowClick(item, $event)" @auxclick="itemRoute(item) && rememberSearch()">
                                <!-- ID first in blue, then Type in black -->
                                <td
                                    class="px-2.5 py-2 bg-white rounded-l-[10px] text-[12px] largeDesktop:text-[14px] text-[#7695FF]">
                                    <component :is="itemRoute(item) ? 'router-link' : 'div'"
                                        :to="itemRoute(item) || undefined" class="block no-underline text-inherit">
                                        <div class="flex items-center gap-2">
                                            {{ item.id }}
                                            <img v-if="item.partner_logo" :src="item.partner_logo" alt="Partner Logo"
                                                class="w-10 h-7 object-contain flex-shrink-0">
                                        </div>
                                    </component>
                                </td>
                                <td
                                    class="px-2.5 py-2 bg-white text-[12px] largeDesktop:text-[14px] text-[#333333] whitespace-nowrap">
                                    <component :is="itemRoute(item) ? 'router-link' : 'div'"
                                        :to="itemRoute(item) || undefined" class="block no-underline text-inherit">
                                        {{ item.type_text || '-' }}
                                    </component>
                                </td>
                                <td class="px-2.5 py-2 bg-white text-[12px] largeDesktop:text-[14px] text-[#333333]">
                                    <component :is="itemRoute(item) ? 'router-link' : 'div'"
                                        :to="itemRoute(item) || undefined" class="block no-underline text-inherit">
                                        <div class="flex items-center gap-2">
                                            <div class="min-w-0">
                                                <p class="m-0">{{ show(item.title) }}</p>
                                                <p v-if="item.address"
                                                    class="m-0 text-[11px] largeDesktop:text-[12px] text-[#999999]">
                                                    {{ item.address }}
                                                </p>
                                            </div>
                                        </div>
                                    </component>
                                </td>
                                <td class="px-2.5 py-2 bg-white text-[12px] largeDesktop:text-[14px] text-[#333333]">
                                    <component :is="itemRoute(item) ? 'router-link' : 'div'"
                                        :to="itemRoute(item) || undefined" class="block no-underline text-inherit">
                                        <p class="m-0">{{ show(item.consumer && item.consumer.name) }}</p>
                                        <p v-if="item.consumer && (item.consumer.phone || item.consumer.email)"
                                            class="m-0 text-[11px] largeDesktop:text-[12px] text-[#999999]">
                                            {{ item.consumer.phone || item.consumer.email }}
                                        </p>
                                    </component>
                                </td>
                                <td
                                    class="px-2.5 py-2 bg-white text-[12px] largeDesktop:text-[14px] text-[#333333] whitespace-nowrap">
                                    <component :is="itemRoute(item) ? 'router-link' : 'div'"
                                        :to="itemRoute(item) || undefined" class="block no-underline text-inherit">
                                        {{ formatDate(item.event_date) }}
                                    </component>
                                </td>
                                <td
                                    class="px-2.5 py-2 bg-white text-[12px] largeDesktop:text-[14px] text-[#333333] whitespace-nowrap">
                                    <component :is="itemRoute(item) ? 'router-link' : 'div'"
                                        :to="itemRoute(item) || undefined" class="block no-underline text-inherit">
                                        {{ formatDate(item.created_at) }}
                                    </component>
                                </td>
                                <td
                                    class="px-2.5 py-2 bg-white text-[12px] largeDesktop:text-[14px] text-[#333333] whitespace-nowrap">
                                    <component :is="itemRoute(item) ? 'router-link' : 'div'"
                                        :to="itemRoute(item) || undefined" class="block no-underline text-inherit">
                                        {{ show(item.amount_text) }}
                                    </component>
                                </td>
                                <td
                                    class="px-2.5 py-2 bg-white rounded-r-[10px] text-[12px] largeDesktop:text-[14px] text-[#333333]">
                                    <component :is="itemRoute(item) ? 'router-link' : 'div'"
                                        :to="itemRoute(item) || undefined" class="block no-underline text-inherit">
                                        <span v-if="item.status_text"
                                            class="text-white rounded-md px-2.5 py-1 block w-fit whitespace-nowrap"
                                            :style="{ backgroundColor: statusColor(item.status_text) }">{{
                                            item.status_text }}</span>
                                        <span v-else>-</span>
                                    </component>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Below laptop: cards -->
                <div class="flex flex-col laptop:hidden">
                    <component :is="itemRoute(item) ? 'router-link' : 'div'" v-for="item in items"
                        :key="item.type + '-' + item.id" :to="itemRoute(item) || undefined"
                        class="block no-underline text-inherit px-2.5 py-1.5 my-0.5 rounded-[10px] border-1 transition-all border-gray-300 bg-white"
                        :class="isClickable(item) ? 'cursor-pointer hover:shadow-md hover:border-purple-300' : 'cursor-default'"
                        @click="onRowClick(item, $event)" @auxclick="itemRoute(item) && rememberSearch()">
                        <div class="flex justify-between items-center gap-2 w-full">
                            <p class="m-0 text-[#333333] font-roboto text-[14px]">
                                {{ item.type_text || '-' }}
                            </p>
                            <span v-if="item.status_text"
                                class="text-white rounded-md px-2 py-[2px] block w-fit font-roboto text-[12px] leading-[16px] flex-shrink-0"
                                :style="{ backgroundColor: statusColor(item.status_text) }">{{ item.status_text
                                }}</span>
                        </div>
                        <div class="flex justify-between items-start gap-2 mt-1">
                            <div class="flex flex-col gap-1 min-w-0">
                                <div>
                                    <span class="block text-[#EA8A8A] font-roboto text-[13px] leading-[18px]">ID:</span>
                                    <p class="m-0 font-roboto text-[14px] leading-[20px] text-[#7695FF]">{{ item.id }}
                                    </p>
                                </div>
                                <div>
                                    <span
                                        class="block text-[#EA8A8A] font-roboto text-[13px] leading-[18px]">Title:</span>
                                    <p class="m-0 font-roboto text-[14px] leading-[20px] break-words">{{
                                        show(item.title) }}</p>
                                </div>
                                <div v-if="item.consumer && item.consumer.name">
                                    <span class="block text-[#EA8A8A] font-roboto text-[13px] leading-[18px]">Consumer
                                        Name:</span>
                                    <p class="m-0 font-roboto text-[14px] leading-[20px] break-words">{{
                                        item.consumer.name }}</p>
                                </div>
                                <div v-if="item.address">
                                    <span
                                        class="block text-[#EA8A8A] font-roboto text-[13px] leading-[18px]">Address:</span>
                                    <p class="m-0 font-roboto text-[14px] leading-[20px] break-words">{{ item.address }}
                                    </p>
                                </div>
                                <div v-if="item.event_date || item.created_at">
                                    <span class="block text-[#EA8A8A] font-roboto text-[13px] leading-[18px]">
                                        {{ item.event_date ? 'Date:' : 'Created:' }}
                                    </span>
                                    <p class="m-0 font-roboto text-[14px] leading-[20px]">
                                        {{ formatDate(item.event_date || item.created_at) }}
                                    </p>
                                </div>
                                <div v-if="item.amount_text">
                                    <span
                                        class="block text-[#EA8A8A] font-roboto text-[13px] leading-[18px]">Amount:</span>
                                    <p class="m-0 font-roboto text-[14px] leading-[20px]">{{ item.amount_text }}</p>
                                </div>
                            </div>
                            <img v-if="item.partner_logo" :src="item.partner_logo" alt="Partner Logo"
                                class="w-12 h-10 object-contain flex-shrink-0">
                        </div>
                    </component>
                </div>
            </template>
        </div>
        <div v-if="items.length > 0"
            class="flex mobile:flex-col tablet:flex-row mobile:gap-3 tablet:gap-0 items-center justify-between relative -top-5 px-3">
            <paginationEntries class="mobile:hidden tablet:block" :current-page="page" :per-page="limit"
                :total-items="totalItems" />
            <customPagination :page="page" :total_pages="total_pages" :update-page="updatePage" />
        </div>
    </div>

    <!-- Protection Plan History Modal -->
    <div id="protectionPlanHistoryCard" ref="protectionPlanHistoryCard" class="modal fade" tabindex="-1"
        aria-labelledby="protectionPlanHistoryCard" aria-hidden="true">
        <div
            class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:!w-[500px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
            <div class="modal-content select-none">
                <div class="modal-body !p-3 !pb-5">
                    <div
                        class="relative w-full mobile:h-[70px] tablet:h-[100px] rounded-lg overflow-hidden flex items-center justify-start">
                        <button type="button"
                            class="absolute top-3 right-4 w-6 h-6 z-50 focus:!outline-none focus:!shadow-none"
                            data-bs-dismiss="modal" aria-label="Close">
                            <font-awesome-icon icon="times" class="w-full h-full text-red-600" />
                        </button>
                        <div class="relative w-full h-full flex flex-col items-center justify-center">
                            <img src="../../assets/images/profile-bg.png"
                                class="w-full object-contain absolute left-0 top-0" alt="backgroundImage">
                            <div class="relative flex justify-between items-center w-full z-10 ps-3 !pe-12">
                                <h6 class="m-0 text-xl font-roboto font-semibold text-black-100">
                                    Details
                                </h6>
                                <img :src="planDetails && planDetails.partner_company_logo" alt="contractor Logo"
                                    class="w-14 h-14 object-contain">
                            </div>
                        </div>
                    </div>
                    <div
                        class="responsiveOreitationHeightAdjust mobile:max-h-[90vh] desktop:max-h-[70vh] largeDesktop:max-h-[90vh] overflow-y-auto customScrollbar mobile:mt-2 tablet:mt-4">
                        <companyDetailDropdown :item="planDetails" />
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Protection Plan Detail Modal -->
    <div id="protectionPlanDetailModal" ref="protectionPlanDetailModal" class="modal fade" tabindex="-1"
        aria-labelledby="protectionPlanDetailModal" aria-hidden="true" data-bs-backdrop="static"
        data-bs-keyboard="false">
        <div
            class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:!w-[500px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
            <div class="modal-content select-none">
                <div v-if="protectionPlanDetails" class="modal-body !p-3 !pb-5">
                    <div class="relative">
                        <button type="button"
                            class="absolute top-3 right-4 w-6 h-6 z-50 focus:!outline-none focus:!shadow-none"
                            data-bs-dismiss="modal" aria-label="Close">
                            <font-awesome-icon icon="times" class="w-full h-full text-red-600" />
                        </button>
                        <div class="relative rounded-lg overflow-hidden h-[100px]">
                            <img src="../../assets/images/profile-bg.png" alt="Background"
                                class="w-full mobile:h-full tablet:h-auto object-cover">
                            <div class="flex flex-col justify-center gap-1 p-3 absolute top-0 left-0 w-full h-full">
                                <div class="flex justify-start items-center gap-1">
                                    <div class="w-1.5 h-1.5 rounded-full bg-purple-100" />
                                    <div class="w-6 h-1.5 rounded-full bg-purple-100" />
                                    <p class=" text-lg font-bold font-roboto pl-2 m-0 text-fuchsia-700">
                                        New Plan
                                    </p>
                                </div>
                                <h4 class="mobile:text-lg table:text-xl m-0 text-black-100">
                                    {{
                                        protectionPlanDetails.plan_name }}
                                </h4>
                            </div>
                        </div>
                    </div>
                    <div
                        class="responsiveOreitationHeightAdjust mobile:max-h-[50vh] mobile:mt-8 tablet:mt-5 tablet:max-h-[70vh] desktop:max-h-[50vh] largeDesktop:max-h-[70vh] pr-2 mb-3 overflow-y-auto customScrollbar">
                        <protectionPlanDetailCard :plan="protectionPlanDetails" />
                    </div>
                    <div class="py-3">
                        <span v-if="protectionPlanDetails.quantity == null || protectionPlanDetails.quantity == 0"
                            class="w-max group px-3 py-1.5 flex justify-center items-center mx-auto gap-1 text-base rounded-full cursor-pointer border-[1px] !border-secondary-100 text-secondary-100 hover:bg-secondary-100 hover:text-white"
                            @click="updateCounter('increment')">
                            <img src="../../assets/images/cart-check.png"
                                class="w-7 h-7 object-contain group-hover:hidden" alt="icon">
                            <img src="../../assets/images/cart-check-white.png"
                                class="w-7 h-7 object-contain !hidden group-hover:!block" alt="icon">
                            Select this plan
                        </span>
                        <div v-else class="flex w-full justify-center items-center gap-4">
                            <quantityCounter v-if="protectionPlanDetails.is_show_quantity == 1"
                                :quantity="protectionPlanDetails.quantity" @update-counter="updateCounter" />
                            <button v-else type="button"
                                class="group h-10 w-10 flex items-center justify-center rounded-full focus:outline-0 transition-all border-1 border-[#F67280] hover:bg-[#F67280]"
                                @click="updateCounter('remove')">
                                <img src="../../assets/images/trash-icon-updated.png" alt="Cart Icon"
                                    class="w-7 h-7 group-hover:hidden">
                                <img src="../../assets/images/trash-icon-white-updated.png" alt="Cart Icon"
                                    class="w-7 h-7 hidden group-hover:!block">
                            </button>
                            <span type="button"
                                class="group h-10 w-10 flex items-center justify-center rounded-full focus:outline-0 transition-all border-1 bg-secondary-100 text-white">
                                <img src="../../assets/images/cart-check-white.png" alt="Cart Icon" class="w-7 h-7">
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Bundle Detail Modal -->
    <bundleDetailModal ref="protectionCarDetailtRef" :is-cart-update="isCartUpdate" @event="updateCart" />
</template>


<script>
import moment from "moment";
import { useToast } from "vue-toastification";
import { mapActions, mapGetters } from "vuex";
import companyDetailDropdown from "../../components/companyDetailModal.vue"
import protectionPlanDetailCard from '../../components/protectionPlansCards/protectionPlanDetailCard.vue';
import customPagination from "../../components/partialComponent/customPagination.vue";
import paginationEntries from "../../components/partialComponent/pagination-entries.vue";
import quantityCounter from '../../components/partialComponent/quantityCounter.vue';
import entities from "../../components/showEntities/entities.vue";
import EmptyState from "../../components/partialComponent/EmptyState.vue";
import bundleDetailModal from "../../components/protectionPlansCards/bundleDetailModal.vue";
import { saveListScroll, takeListScroll, cameBack, restoreListScroll } from "../../helpers/scrollRestore";
import { searchApi, searchStatusColor, SEARCH_MIN_LENGTH, SEARCH_MAX_LENGTH, SEARCH_TYPE_FILTERS } from "../../services/searchApi";

const toast = useToast();

// Row type → detail route; consumers have no page in tech-web, plans/bundles/sales open the modals below
const DETAIL_ROUTES = {
    sar: "job-detail",
    rq: "rq-detail",
    uq: "QuoteDetail",
    appointment: "AppointmentDetail",
};
const MODAL_TYPES = ["protection_plan_sale", "protection_plan", "protection_plan_bundle"];

export default {
    components: {
        companyDetailDropdown,
        protectionPlanDetailCard,
        customPagination,
        paginationEntries,
        quantityCounter,
        entities,
        EmptyState,
        bundleDetailModal
    },
    data() {
        return {
            limit: 25,
            page: 1,
            total_pages: 0,
            planDetails: null,
            loading: false,
            loaded: false,
            requestId: 0,
            items: [],
            typeFilters: SEARCH_TYPE_FILTERS,
            selectedTypes: [], // empty = all types
            typeMenuOpen: false,
            minLength: SEARCH_MIN_LENGTH,
            headers: [
                { key: "id", text: "ID" },
                { key: "type", text: "Type" },
                { key: "title", text: "Title" },
                { key: "consumer", text: "Consumer" },
                { key: "event_date", text: "Date" },
                { key: "created_at", text: "Created" },
                { key: "amount", text: "Amount" },
                { key: "status", text: "Status" },
            ],
            totalItems: 0,
            isOpenFromCart: false,
            isCartUpdate: false,
            plans: [],
            cartCount: {},
            updateCard: null,
            updateCardCart: null,
            selectedCartType: 'plan',
            selectedPlan: {},
        };
    },
    computed: {
        // the dropdown rows: every type, no "All" row (nothing ticked already means all)
        typeOptions() {
            return this.typeFilters.filter(f => f.key !== 'all')
        },
        allTypesSelected() {
            return this.typeOptions.length > 0 && this.typeOptions.every(f => this.selectedTypes.includes(f.key))
        },
        // type dropdown button: All types / the one type / "N types"
        typeLabel() {
            if (!this.selectedTypes.length || this.allTypesSelected) return 'All types'
            if (this.selectedTypes.length === 1) return this.typeFilters.find(f => f.key === this.selectedTypes[0])?.text || '1 type'
            return `${this.selectedTypes.length} types`
        },
        ...mapGetters([
            "salesPlanDetails",
            "protectionPlanDetails",
            "cartUpdated",
            "cartValidate",
            "removeProtectionCartData",
        ]),
        query() {
            return (this.$route.query.q || "").toString().trim();
        },
        tooShort() {
            return this.query.length > 0 && this.query.length < SEARCH_MIN_LENGTH;
        },
    },
    // back from a detail page → same scroll position once the results are in (helpers/scrollRestore.js)
    beforeRouteLeave(to, from, next) {
        saveListScroll(from.fullPath);
        next();
    },
    watch: {
        loading(isLoading) {
            if (isLoading || this.pendingListScroll == null) return
            const y = this.pendingListScroll;
            this.pendingListScroll = null;
            this.$nextTick(() => restoreListScroll(y));
        },

        removeProtectionCartData: function (data) {

            if (this.selectedCartType == 'bundle') {
                this.upsertCart({
                    "bundle_id": this.selectedPlan.id,
                    "quantity": this.selectedPlan.quantity,
                    "partner_id": this.planDetails.partner_id
                })
            }
            else {
                this.upsertCart({
                    "plan_id": this.selectedPlan.id,
                    "quantity": this.selectedPlan.quantity,
                    "partner_id": this.planDetails.partner_id
                })
            }

        },
        cartValidate: function (data) {

            if (this.cartValidate.validation_passed) {

                if (this.selectedCartType == 'bundle') {
                    this.upsertCart({
                        "bundle_id": this.selectedPlan.id,
                        "quantity": this.selectedPlan.quantity,
                        "partner_id": this.planDetails.partner_id
                    })
                }
                else {
                    this.upsertCart({
                        "plan_id": this.selectedPlan.id,
                        "quantity": this.selectedPlan.quantity,
                        "partner_id": this.planDetails.partner_id
                    })
                }

            }
            else {

                this.$confirmDialog.show({
                    title: "Action Required",
                    message: this.cartValidate.message,

                    onConfirm: () => {
                        this.removeBundle(this.cartValidate)
                    },
                });
            }

        },

        // The URL holds the whole search: ?q=…&page=…&limit=…&types=sar,rq (t from the header forces a reload of the same
        // text). Paging / filters / per-page only change the query, so coming back from a detail page restores them.
        '$route.query': {
            immediate: true,
            handler() {
                if (this.$route.name !== 'globalSearch') return
                const q = this.$route.query
                const limit = Number(q.limit)
                this.limit = [25, 50, 100].includes(limit) ? limit : 25
                const keys = this.typeFilters.map(f => f.key).filter(k => k !== 'all')
                this.selectedTypes = (q.types || '').toString().split(',').filter(k => keys.includes(k))
                this.fetch(Math.max(1, parseInt(q.page, 10) || 1))
            },
        },

        cartUpdated: function (data) {
            this.getCartCount({
                is_hide: true
            });
        },

        salesPlanDetails: function (data) {
            this.planDetails = this.salesPlanDetails
            const { Modal } = require('bootstrap');
            const modalEl = this.$refs.protectionPlanHistoryCard;
            this.Modal = Modal.getInstance(modalEl);
            if (!this.Modal) {
                this.Modal = new Modal(modalEl, {
                    backdrop: 'static',
                    keyboard: false
                });
            }
            modalEl.addEventListener('show.bs.modal', () => {
                document.documentElement.style.overflow = 'hidden'; // Target HTML element
                if (window.innerWidth >= 992) {
                    document.documentElement.style.paddingRight = '15px';
                } else {
                    document.documentElement.style.paddingRight = '0px';
                }
                this.adjustModalHeightprotectionPlanHistory();
            });
            modalEl.addEventListener('hidden.bs.modal', () => {
                document.documentElement.style.overflow = ''; // Reset HTML element
                document.documentElement.style.paddingRight = '0px';
                window.removeEventListener('resize', this.adjustModalHeightprotectionPlanHistory);
                window.removeEventListener('orientationchange', this.adjustModalHeightprotectionPlanHistory);
            });
            window.addEventListener('resize', this.adjustModalHeightprotectionPlanHistory);
            window.addEventListener('orientationchange', this.adjustModalHeightprotectionPlanHistory);
            this.Modal.show();
        },

        protectionPlanDetails: function (data) {
            const { Modal } = require('bootstrap');
            const modalEl = this.$refs.protectionPlanDetailModal;

            this.modal = Modal.getInstance(modalEl);
            if (!this.modal) {
                this.modal = new Modal(modalEl, {
                    backdrop: 'static',
                    keyboard: false
                });
            }
            // Add event listeners for show/hide
            modalEl.addEventListener('show.bs.modal', () => {
                document.documentElement.style.overflow = 'hidden'; // Target HTML element
                if (window.innerWidth >= 992) {
                    document.documentElement.style.paddingRight = '15px';
                } else {
                    document.documentElement.style.paddingRight = '0px';
                }
                this.adjustModalHeightprotectionPlanDetail();
            });

            modalEl.addEventListener('hidden.bs.modal', () => {
                document.documentElement.style.overflow = ''; // Reset HTML element
                document.documentElement.style.paddingRight = '0px';
                window.removeEventListener('resize', this.adjustModalHeightprotectionPlanDetail);
                window.removeEventListener('orientationchange', this.adjustModalHeightprotectionPlanDetail);
            });

            // Add orientation/resize responsiveness
            window.addEventListener('resize', this.adjustModalHeightprotectionPlanDetail);
            window.addEventListener('orientationchange', this.adjustModalHeightprotectionPlanDetail);
            this.modal.show();
            this.$refs.protectionPlanDetailModal.addEventListener('hidden.bs.modal', this.onCartModalClose);
        },

    },
    created() {
        this.pendingListScroll = cameBack() ? takeListScroll(this.$route.fullPath) : null
    },
    mounted() {
        document.body.style.overflow = 'hidden';
        document.addEventListener('click', this.closeTypeMenu);
    },
    beforeUnmount() {
        document.body.style.overflow = '';
        document.removeEventListener('click', this.closeTypeMenu);
    },
    methods: {
        ...mapActions([
            "getSalesPlanDetails",
            "getProtectionPlanDetails",
            "upsertCart",
            "getCartCount",
            "validateCart",
            "removeProtectionItemCart"
        ]),

        removeBundle(data) {
            this.removeProtectionItemCart({
                "bundle_ids": data.bundle_ids,
                "plan_ids": data.plan_ids,
            })
        },
        adjustModalHeightprotectionPlanDetail() {
            const modalContent = this.$refs.protectionPlanDetailModal?.querySelector('.responsiveOreitationHeightAdjust');
            if (modalContent) {
                const vh = window.innerHeight;
                const vw = window.innerWidth;
                let maxHeight;
                if (vw < 400) {
                    // Mobile devices
                    maxHeight = vh * 0.8 - 40; // 90% of viewport height minus some padding
                } else if (vw < 768) {
                    // Mobile devices
                    maxHeight = vh * 0.7 - 40; // 90% of viewport height minus some padding
                } else if (vw < 1024) {
                    // Tablets
                    maxHeight = vh * 0.4 - 30;
                } else {
                    // Desktops
                    maxHeight = vh * 0.7 - 40;
                }
                modalContent.style.maxHeight = `${maxHeight}px`;
                modalContent.style.overflowY = 'auto';
            }
        },
        adjustModalHeightprotectionPlanHistory() {
            const modalContent = this.$refs.protectionPlanHistoryCard?.querySelector('.responsiveOreitationHeightAdjust');
            if (modalContent) {
                const vh = window.innerHeight;
                const vw = window.innerWidth;
                let maxHeight;
                if (vw < 400) {
                    // Mobile devices
                    maxHeight = vh * 0.8 - 40; // 90% of viewport height minus some padding
                } else if (vw < 768) {
                    // Mobile devices
                    maxHeight = vh * 0.9 - 40; // 90% of viewport height minus some padding
                } else if (vw < 1024) {
                    // Tablets
                    maxHeight = vh * 0.6 - 30;
                } else {
                    // Desktops
                    maxHeight = vh * 0.8 - 40;
                }
                modalContent.style.maxHeight = `${maxHeight}px`;
                modalContent.style.overflowY = 'auto';
            }
        },
        async fetch(page) {
            const search = this.query
            const id = ++this.requestId
            if (!search || search.length < SEARCH_MIN_LENGTH) {
                this.items = []
                this.page = 1
                this.total_pages = 0
                this.totalItems = 0
                this.loaded = false
                this.loading = false
                return
            }
            this.loading = true
            const res = await searchApi.all({
                search: search.slice(0, SEARCH_MAX_LENGTH),
                types: this.selectedTypes,
                page,
                limit: this.limit,
            })
            if (id !== this.requestId) return // a newer search/filter/page replaced this one
            this.loading = false
            this.loaded = true
            if (!res.ok) {
                toast.error(res.message || "Unable to search. Please try again.")
                this.items = []
                this.total_pages = 0
                this.totalItems = 0
                return
            }
            const rows = Array.isArray(res.body) ? res.body : []
            this.items = rows
            this.page = page
            this.total_pages = Number(res.paging?.total_pages) || (rows.length ? page : 0)
            this.totalItems = Number(res.paging?.total_records) || rows.length
        },
        closeTypeMenu(event) {
            if (this.typeMenuOpen && !this.$refs.typeMenu?.contains(event.target)) this.typeMenuOpen = false
        },
        isTypeSelected(key) {
            return this.selectedTypes.includes(key)
        },
        // tick / untick a type; none ticked = no types[] filter (all types)
        onSelectAllTypes(event) {
            this.selectedTypes = event.target.checked ? this.typeOptions.map(f => f.key) : []
            this.setQuery({ types: this.selectedTypes.length ? this.selectedTypes.join(',') : undefined, page: undefined })
        },
        onTypeChange(key, event) {
            this.selectedTypes = event.target.checked
                ? [...this.selectedTypes, key]
                : this.selectedTypes.filter(k => k !== key)
            this.setQuery({ types: this.selectedTypes.length ? this.selectedTypes.join(',') : undefined, page: undefined })
        },
        // update the search URL (replace: paging / filters don't add history entries); the $route.query watcher fetches
        setQuery(changes) {
            this.$router.replace({ name: 'globalSearch', query: { ...this.$route.query, ...changes } })
        },
        isClickable(item) {
            return !!DETAIL_ROUTES[item.type] || MODAL_TYPES.includes(item.type)
        },
        // detail types → route location (rendered as a real link, so it can open in a new tab); null for modal types
        itemRoute(item) {
            const route = item && item.id && DETAIL_ROUTES[item.type]
            // from=search: the detail page shows "Back to search results" (BackToSearch.vue); back = this results page,
            // carried in the link so it also works in a new tab / after a refresh (sessionStorage doesn't reach a new tab)
            return route ? { name: route, params: { id: item.id }, query: { from: 'search', back: this.$route.fullPath } } : null
        },
        rememberSearch() {
            sessionStorage.setItem('search_return_path', this.$route.fullPath)
        },
        // clicks on the <a> are handled by router-link; padding around it / modal types land here
        onRowClick(item, e) {
            const link = e && e.target.closest && e.target.closest('a')
            if (link) {
                if (this.itemRoute(item)) this.rememberSearch()
                return
            }
            this.openItem(item)
        },
        openItem(item) {
            if (!item || !item.id) return
            const to = this.itemRoute(item)
            if (to) {
                this.rememberSearch()
                this.$router.push(to)
            } else if (item.type === 'protection_plan_sale') {
                this.getSalesPlanDetails({ id: item.id })
            } else if (item.type === 'protection_plan') {
                this.getProtectionPlanDetails({ id: item.id })
            } else if (item.type === 'protection_plan_bundle') {
                this.$refs.protectionCarDetailtRef.openCartModal(item)
            }
        },
        show(value) {
            return this.$helper.isEmpty(value) ? '-' : value
        },
        formatDate(seconds) {
            return seconds ? moment.unix(seconds).format('MM/DD/YYYY hh:mm A') : '-'
        },
        statusColor(text) {
            return searchStatusColor(text)
        },
        updatePage(page) {
            if (page == this.page) return
            this.setQuery({ page: page > 1 ? String(page) : undefined })
            this.$nextTick(() => {
                document.documentElement.scrollTo({ top: 0, behavior: "smooth" })
            })
        },

        updateCounter(option) {
            if (option == "increment") {
                this.protectionPlanDetails.quantity++
            } else if (option == "decrement") {
                if (this.protectionPlanDetails.quantity > 1) {
                    this.protectionPlanDetails.quantity--
                } else {
                    this.protectionPlanDetails.quantity = 0
                }
            } else {
                this.protectionPlanDetails.quantity = 0
            }
            this.updateCart(option, {
                id: this.protectionPlanDetails.id,
                quantity: this.protectionPlanDetails.quantity,
                type: 'plan'
            })
        },

        updateCart(option, plan, type = 'plan', action, details) {

            this.planDetails = details
            this.selectedPlan = plan
            this.selectedCartType = type

            if (option == "cartUpdate") {
                this.isCartUpdate = true
            } else {
                this.isCartUpdate = false
            }

            if (option == 'decrement' || option == 'remove') {

                if (type == 'bundle') {
                    this.upsertCart({
                        "bundle_id": plan.id,
                        "quantity": plan.quantity,
                        "partner_id": details.partner_id
                    })
                }
                else {
                    this.upsertCart({
                        "plan_id": plan.id,
                        "quantity": plan.quantity,
                        "partner_id": details.partner_id
                    })
                }

            }
            else {

                if (type == 'bundle') {
                    this.validateCart({
                        "bundle_id": plan.id,
                        "quantity": plan.quantity,
                        "partner_id": details.partner_id
                    })
                }
                else {
                    this.validateCart({
                        "plan_id": plan.id,
                        "quantity": plan.quantity,
                        "partner_id": details.partner_id
                    })
                }
            }

        },
        onLimitChange(newLimit) {
            this.setQuery({ limit: newLimit != 25 ? String(newLimit) : undefined, page: undefined })
        }
    }
}
</script>